import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/Button';
import { RefreshCw } from 'lucide-react';
import { cachedGet } from '@/utils/cache';
import { isAuthExpired, isAuthExpiredError } from '@/utils/authFetch';
import { API_BASE_URL } from '@/config';

type GridMetrics = {
  trade_id?: string | null;
  symbol?: string | null;
  side?: string | null;
  levels?: number;
  filled_levels?: number;
  remaining_levels?: number;
  next_trigger_price?: number | null;
  entry_price?: number | null;
  take_profit?: number | null;
  stop_loss?: number | null;
  quantity?: number | null;
  tp_distance_pct?: number;
  mark_price?: number | null;
};

type GridMetricsResponse = {
  success: boolean;
  grid: GridMetrics | null;
  error?: string;
};

export interface GridMetricsPanelProps {
  refreshTrigger?: number;
  className?: string;
  initialData?: GridMetricsResponse | null;
  canonicalBtcMarkPrice?: number | null;
}

export const GridMetricsPanel = memo(function GridMetricsPanel({
  refreshTrigger,
  className,
  initialData = null,
  canonicalBtcMarkPrice = null,
}: GridMetricsPanelProps) {
  const [data, setData] = useState<GridMetricsResponse | null>(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);
  const lastRefreshTriggerRef = useRef<number | undefined>(refreshTrigger);
  const failStreakRef = useRef(0);

  const fetchMetrics = useCallback(async () => {
    if (isAuthExpired()) {
      setData({ success: false, grid: null, error: 'Session expired. Sign in again to continue.' });
      setLoading(false);
      setIsRefreshing(false);
      hasLoadedRef.current = true;
      return;
    }

    const isInitialLoad = !hasLoadedRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    try {
      const res = await cachedGet<GridMetricsResponse>(`${API_BASE_URL}/api/trading/grid-metrics`, {
        ttl: 1000,
        forceRefresh: true,
      });
      failStreakRef.current = 0;
      setData(res);
    } catch (e) {
      failStreakRef.current += 1;
      const errorMessage = isAuthExpiredError(e)
        ? 'Session expired. Sign in again to continue.'
        : e instanceof Error
          ? e.message
          : 'Failed to load grid metrics';
      setData({ success: false, grid: null, error: errorMessage });
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (initialData && !hasLoadedRef.current) {
      setData(initialData);
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [initialData]);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      void fetchMetrics();
      return;
    }

    if (refreshTrigger !== lastRefreshTriggerRef.current) {
      lastRefreshTriggerRef.current = refreshTrigger;
      void fetchMetrics();
    }
  }, [fetchMetrics, refreshTrigger]);

  useEffect(() => {
    // 12s poll (offset from the 10s price / 30s signal loops): skips hidden
    // tabs and backs off after consecutive failures so backend slots herd less.
    const interval = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      if (failStreakRef.current >= 3) {
        failStreakRef.current = 0;
        return;
      }
      if (!isAuthExpired()) {
        void fetchMetrics();
      }
    }, 12000);

    return () => {
      window.clearInterval(interval);
    };
  }, [fetchMetrics]);

  const grid = data?.grid ?? null;
  const isBtcGrid = (grid?.symbol?.toUpperCase() ?? '') === 'BTCUSDT';
  const displayedMarkPrice = isBtcGrid && canonicalBtcMarkPrice !== null ? canonicalBtcMarkPrice : (grid?.mark_price ?? null);

  const hasGrid = Boolean(grid && grid.levels && grid.levels > 0);

  const formatNumber = useCallback((value: number | null | undefined, decimals: number = 2) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '--';
    return value.toFixed(decimals);
  }, []);

  const header = useMemo(() => {
    if (loading) return 'Grid Metrics (loading…)';
    if (data && data.success === false) return 'Grid Metrics (error)';
    if (!hasGrid) return 'Grid Metrics';
    return 'Grid Metrics';
  }, [data, hasGrid, loading]);

  return (
    <Card className={`${className ?? ''} w-full min-w-0 max-w-full overflow-hidden`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-muted-foreground truncate">{header}</h3>
            {grid?.symbol ? (
              <div className="text-xs text-muted-foreground truncate">
                {grid.symbol} {grid.side ? `(${grid.side})` : ''}
              </div>
            ) : null}
          </div>
          <Button variant="outline" size="sm" onClick={() => void fetchMetrics()}>
            <RefreshCw className={`w-4 h-4 ${loading || isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {data?.success === false ? (
          <div className="text-xs text-red-600 break-words">{data.error || 'Failed to load grid metrics'}</div>
        ) : hasGrid ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Progress</div>
              <div className="text-sm font-semibold">
                {grid?.filled_levels ?? 0}/{grid?.levels ?? 0}
              </div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Next Trigger</div>
              <div className="text-sm font-semibold">{formatNumber(grid?.next_trigger_price ?? null, 2)}</div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Mark</div>
              <div className="text-sm font-semibold">{formatNumber(displayedMarkPrice, 2)}</div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Qty</div>
              <div className="text-sm font-semibold">{formatNumber(grid?.quantity ?? null, 6)}</div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Entry</div>
              <div className="text-sm font-semibold">{formatNumber(grid?.entry_price ?? null, 2)}</div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">TP</div>
              <div className="text-sm font-semibold">{formatNumber(grid?.take_profit ?? null, 2)}</div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">SL</div>
              <div className="text-sm font-semibold">{formatNumber(grid?.stop_loss ?? null, 2)}</div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">TP Dist</div>
              <div className="text-sm font-semibold">{formatNumber(((grid?.tp_distance_pct ?? 0) * 100) || 0, 2)}%</div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">No active grid trade.</div>
        )}
      </CardContent>
    </Card>
  );
});

export default GridMetricsPanel;
